import React, { memo } from 'react'
import tw from 'twin.macro'
import dayjs from 'dayjs'
import { useFormikContext } from 'formik'
import { ReviewDateField } from 'src/components/document-review/review/ReviewDateField'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { REVIEW_ADDRESS_STATE_OPTIONS } from 'src/constants/document-review/constants'
import type { ReviewFormValues } from 'src/interfaces/documentReview'
import { resolveReviewDateBounds } from 'src/utils/documentReviewDates'

interface Props {
  isExpirationRequired: boolean
  /** Shows the address and makes the names, address and expiry required. */
  isTemporaryDriverLicense: boolean
}

const markRequired = (label: string, isRequired: boolean): string => {
  return isRequired ? `${label} *` : label
}

const ContractorReviewFormContentComponent = ({
  isExpirationRequired,
  isTemporaryDriverLicense,
}: Props) => {
  const { values } = useFormikContext<ReviewFormValues>()
  const { expirationMin, effectiveMin, effectiveMax } = resolveReviewDateBounds({
    dateIssued: values.dateIssued,
    expiresAt: values.expiresAt,
  })

  return (
    <div css={tw`flex max-w-2xl flex-col gap-4`}>
      <div css={tw`grid grid-cols-2 gap-4`}>
        <FormikTextField<ReviewFormValues>
          name="firstName"
          label={markRequired('First name', isTemporaryDriverLicense)}
          placeholder="First name"
        />
        <FormikTextField<ReviewFormValues>
          name="lastName"
          label={markRequired('Last name', isTemporaryDriverLicense)}
          placeholder="Last name"
        />
      </div>
      <FormikTextField<ReviewFormValues> name="documentId" label="Document ID" />
      <div css={tw`grid grid-cols-3 gap-4`}>
        <ReviewDateField<ReviewFormValues> name="dateIssued" label="Issue date" />
        <ReviewDateField<ReviewFormValues>
          name="expiresAt"
          label={markRequired('Expiration date', isExpirationRequired || isTemporaryDriverLicense)}
          minDate={dayjs(expirationMin)}
        />
        <ReviewDateField<ReviewFormValues>
          name="effectiveAt"
          label="Effective date"
          minDate={dayjs(effectiveMin)}
          maxDate={effectiveMax ? dayjs(effectiveMax) : undefined}
        />
      </div>
      {isTemporaryDriverLicense ? (
        <div css={tw`flex flex-col gap-4`} data-testid="document-review-temporary-license-address">
          <div css={tw`grid grid-cols-2 gap-4`}>
            <FormikTextField<ReviewFormValues>
              name="addressStreet1"
              label="Street 1 *"
              placeholder="Street 1"
            />
            <FormikTextField<ReviewFormValues> name="addressStreet2" label="Street 2" />
          </div>
          <div css={tw`grid grid-cols-3 gap-4`}>
            <FormikTextField<ReviewFormValues>
              name="addressCity"
              label="City *"
              placeholder="City"
            />
            <FormikSelect<ReviewFormValues>
              name="addressState"
              label="State *"
              placeholder="Select a state"
              options={REVIEW_ADDRESS_STATE_OPTIONS}
            />
            <FormikTextField<ReviewFormValues>
              name="addressPostalCode"
              label="Postal code *"
              placeholder="Postal code"
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}

export const ContractorReviewFormContent = memo(ContractorReviewFormContentComponent)
